import { useState, useRef } from 'react';
import TextField from '@mui/material/TextField';
import {
  Accordion, AccordionSummary, AccordionDetails, Typography, Box, IconButton, InputAdornment, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import openDatePicker from '../common/util/openDatePicker';
import { MuiFileInput } from 'mui-file-input';
import InfoIcon from '@mui/icons-material/Info';
import DriverIcon from '@mui/icons-material/Description';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import { useTranslation } from '../common/components/LocalizationProvider';
import SettingsMenu from './components/SettingsMenu';
import useSettingsStyles from './common/useSettingsStyles';
import { useLocalization } from '../common/components/LocalizationProvider';

const DriverPage = () => {
  const { classes } = useSettingsStyles();
  const t = useTranslation();
  const { htmlLang } = useLocalization();
  const theme = useTheme();

  const [item, setItem] = useState();
  const documentValidAtRef = useRef(null);

  const filterAttributes = (attributes) => {
    if (!attributes) return {};
    const filtered = {};
    Object.keys(attributes).forEach((key) => {
      const value = attributes[key];
      if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  const ensureFormStructure = (currentItem) => {
    if (!currentItem) return currentItem;
    return {
      ...currentItem,
      attributes: {
        ...currentItem.attributes,
        form: currentItem.attributes?.form || {},
      },
    };
  };

  const validate = () => item && item.name && item.uniqueId;

  return (
    <EditItemView
      endpoint="drivers"
      item={item}
      setItem={(newItem) => setItem(ensureFormStructure(newItem))}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedDriver']}
    >
      {item && (
        <>
          {/* SEÇÃO 1: CAMPOS OBRIGATÓRIOS */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon color="primary" />
                <Typography variant="subtitle1">{t('sharedRequired')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box display="flex" width="100%" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                <Box flex={2} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item.name || ''}
                    onChange={(e) => setItem({ ...item, name: e.target.value })}
                    label={t('sharedName')}
                    required
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Box flex={1} minWidth={0}>
                  <TextField
                    fullWidth
                    value={item.uniqueId || ''}
                    onChange={(e) => setItem({ ...item, uniqueId: e.target.value })}
                    label={t('sharedDriver')}
                    required
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* SEÇÃO 2: PREFERÊNCIAS / DADOS PESSOAIS */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <InfoIcon color="primary" />
                <Typography variant="subtitle1">{t('sharedPreferences')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={2}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.email || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, email: e.target.value } } })}
                      label={t('sharedEmail')}
                      type="email"
                    />
                  </Box>
                  <Box flex={1}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.taxId || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, taxId: e.target.value } } })}
                      label={t('attributeItin')}
                    />
                  </Box>
                  <Box flex={1}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.phone || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, phone: e.target.value } } })}
                      label={t('sharedPhone')}
                      type="tel"
                    />
                  </Box>
                </Box>

                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.contact || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, contact: e.target.value } } })}
                      label={t('deviceContact')}
                    />
                  </Box>
                  <Box flex={2}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.address || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, address: e.target.value } } })}
                      label={t('sharedAddress')}
                    />
                  </Box>
                </Box>

                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1.5}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.neighborhood || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, neighborhood: e.target.value } } })}
                      label={t('sharedNeighborhood')}
                    />
                  </Box>
                  <Box flex={1.5}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.city || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, city: e.target.value } } })}
                      label={t('sharedCity')}
                    />
                  </Box>
                  <Box flex={0.6}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.state || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, state: e.target.value } } })}
                      label={t('sharedState')}
                    />
                  </Box>
                  <Box flex={1}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.postCode || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, postCode: e.target.value } } })}
                      label={t('sharedPostalCode')}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* SEÇÃO 3: CNH */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <DriverIcon color="primary" />
                <Typography variant="subtitle1">{t('sharedDriverLicence')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={2}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.document || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, document: e.target.value } } })}
                      label={t('sharedDocumentCNH')}
                      placeholder="Ex: 12345678900"
                    />
                  </Box>
                  <Box flex={1}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.documentCategory || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, documentCategory: e.target.value } } })}
                      label={t('deviceCategory')}
                      placeholder="Ex: AB"
                    />
                  </Box>
                </Box>

                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1.5}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.documentValidAt || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, documentValidAt: e.target.value } } })}
                      label={t('sharedDocumentValidDate')}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: { type: 'date', lang: htmlLang },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" edge="end" onClick={() => openDatePicker(documentValidAtRef.current)}>
                              <CalendarTodayIcon sx={{ color: theme.palette.action.active }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      inputRef={documentValidAtRef}
                      className={classes.dateInput}
                    />
                  </Box>
                  <Box flex={0.5}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.bloodType || ''}
                      onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, form: { ...item.attributes.form, bloodType: e.target.value } } })}
                      label={t('sharedBloodType')}
                      placeholder="Ex: O+"
                    />
                  </Box>
                </Box>

                {item.id && (
                  <Box mt={2}>
                    <MuiFileInput
                      fullWidth
                      label={t('attributeDl')}
                      placeholder="Clique aqui para anexar cópia da CNH"
                      value={null}
                      onChange={(file) => console.log('Implementar upload', file)}
                      inputProps={{ accept: 'image/*,.pdf' }}
                    />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <EditAttributesAccordion
            attributes={filterAttributes(item.attributes)}
            setAttributes={(attributes) => setItem({ ...item, attributes: { ...item.attributes, ...attributes } })}
            definitions={{}}
          />
        </>
      )}
    </EditItemView>
  );
};

export default DriverPage;  